import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ColumnDef,
  PaginationState,
  Row,
  RowSelectionState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { FiMenu } from 'react-icons/fi';
import { Dropdown } from './Modal/Body';

export interface DataTableColumn<TData> {
  id: string;
  header: ReactNode;
  accessorKey?: keyof TData;
  cell?: (row: TData, rowIndex: number) => ReactNode;
}

interface DataTableProps<TData> {
  columns: DataTableColumn<TData>[];
  data: TData[];
  enableRowDragAndDrop?: boolean;
  enableRowSelection?: boolean;
  enablePagination?: boolean;
  enablePageSizeSelector?: boolean;
  initialPageSize?: number;
  manualPagination?: boolean;
  totalRows?: number;
  pageCount?: number;
  pagination?: PaginationState;
  onPaginationChange?: (pagination: PaginationState) => void;
  disabled?: boolean;
  getRowId?: (row: TData, index: number) => string;
  onDataChange?: (data: TData[]) => void;
  onSelectionChange?: (selectedRows: TData[]) => void;
  emptyMessage?: string;
  tableStyle?: React.CSSProperties;
}

interface IndeterminateCheckboxProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  indeterminate?: boolean;
}

const IndeterminateCheckbox: React.FC<IndeterminateCheckboxProps> = ({
  indeterminate,
  ...props
}) => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = Boolean(indeterminate);
    }
  }, [indeterminate]);

  return <input ref={ref} type="checkbox" {...props} />;
};

interface DataTableRowProps<TData> {
  row: Row<TData>;
  enableDrag: boolean;
  enableSelection: boolean;
  onSelectionChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

function DataTableRow<TData>({
  row,
  enableDrag,
  enableSelection,
  onSelectionChange,
}: DataTableRowProps<TData>) {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.id, disabled: !enableDrag });

  return (
    <tr
      ref={setNodeRef}
      style={{
        backgroundColor: isDragging
          ? '#edf5e9'
          : row.index % 2 === 0
          ? '#fff'
          : '#f7f7f7',
        borderBottom: '1px solid #e5e5e5',
        opacity: isDragging ? 0.8 : 1,
        position: 'relative',
        transform: CSS.Translate.toString(transform),
        transition,
        zIndex: isDragging ? 1 : 0,
      }}
    >
      {enableSelection && (
        <td style={{ padding: '6px 8px', width: 40 }}>
          <input
            type="checkbox"
            aria-label="Selecionar linha"
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onChange={onSelectionChange}
          />
        </td>
      )}
      {row.getVisibleCells().map((cell, index) => (
        <td key={cell.id} style={{ padding: '6px 8px', textAlign: 'left' }}>
          {enableDrag && index === 0 && (
            <button
              ref={setActivatorNodeRef}
              type="button"
              aria-label="Arrastar para reordenar"
              {...attributes}
              {...listeners}
              style={{
                background: 'none',
                border: 0,
                cursor: 'grab',
                marginRight: 8,
                padding: 0,
                touchAction: 'none',
              }}
            >
              <FiMenu aria-hidden="true" />
            </button>
          )}
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      ))}
    </tr>
  );
}

export function DataTable<TData>({
  columns,
  data,
  enableRowDragAndDrop = false,
  enableRowSelection = false,
  enablePagination = false,
  enablePageSizeSelector = true,
  initialPageSize = 10,
  manualPagination = false,
  totalRows,
  pageCount,
  pagination: controlledPagination,
  onPaginationChange,
  disabled = false,
  getRowId,
  onDataChange,
  onSelectionChange,
  emptyMessage = 'Nenhum registro encontrado.',
  tableStyle,
}: DataTableProps<TData>) {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [internalPagination, setInternalPagination] = useState<PaginationState>(
    {
      pageIndex: 0,
      pageSize: initialPageSize,
    }
  );
  const pagination = controlledPagination ?? internalPagination;
  const usesManualPagination = manualPagination || totalRows !== undefined;
  const effectivePageCount =
    totalRows !== undefined
      ? Math.ceil(totalRows / pagination.pageSize)
      : pageCount;
  const selectionCallbackRef = useRef(onSelectionChange);
  selectionCallbackRef.current = onSelectionChange;

  const columnDefinitions = useMemo<ColumnDef<TData, unknown>[]>(
    () =>
      columns.map((column) => ({
        id: column.id,
        accessorFn: (row) =>
          column.accessorKey ? row[column.accessorKey] : undefined,
        header: () => column.header,
        cell: ({ row, getValue }) =>
          column.cell
            ? column.cell(row.original, row.index)
            : String(getValue() ?? ''),
      })),
    [columns]
  );

  const table = useReactTable({
    data,
    columns: columnDefinitions,
    state: { rowSelection, ...(enablePagination ? { pagination } : {}) },
    getRowId: (row, index) => getRowId?.(row, index) ?? String(index),
    enableRowSelection: enableRowSelection && !disabled,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: (updater) => {
      const nextPagination =
        typeof updater === 'function' ? updater(pagination) : updater;
      if (!controlledPagination) {
        setInternalPagination(nextPagination);
      }
      onPaginationChange?.(nextPagination);
    },
    manualPagination: usesManualPagination,
    pageCount: usesManualPagination ? effectivePageCount : undefined,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: enablePagination
      ? getPaginationRowModel()
      : undefined,
  });

  const canDrag = enableRowDragAndDrop && !disabled;
  const canSelect = enableRowSelection && !disabled;
  const rowIds = table.getRowModel().rows.map((row) => row.id);
  const visiblePages = getVisiblePages(
    table.getPageCount(),
    pagination.pageIndex
  );
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor)
  );

  useEffect(() => {
    if (enableRowSelection) {
      selectionCallbackRef.current?.(
        table.getSelectedRowModel().rows.map((row) => row.original)
      );
    }
  }, [data, enableRowSelection, rowSelection, table]);

  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    if (!over || active.id === over.id || !onDataChange) {
      return;
    }

    const rows = table.getRowModel().rows;
    const activeRow = rows.find((row) => row.id === String(active.id));
    const overRow = rows.find((row) => row.id === String(over.id));
    if (!activeRow || !overRow) {
      return;
    }

    onDataChange(arrayMove(data, activeRow.index, overRow.index));
  }

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <table
        style={{
          borderCollapse: 'collapse',
          textAlign: 'left',
          width: '100%',
          ...tableStyle,
        }}
      >
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {canSelect && (
                <th scope="col" style={{ padding: '10px 8px', width: 40 }}>
                  <IndeterminateCheckbox
                    aria-label="Selecionar todas as linhas"
                    checked={table.getIsAllRowsSelected()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    disabled={data.length === 0}
                    onChange={table.getToggleAllRowsSelectedHandler()}
                  />
                </th>
              )}
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  scope="col"
                  colSpan={header.colSpan}
                  style={{
                    borderBottom: '1px solid #d9d9d9',
                    color: '#444',
                    fontSize: 13,
                    padding: '10px 8px',
                    textAlign: 'left',
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <SortableContext
          items={canDrag ? rowIds : []}
          strategy={verticalListSortingStrategy}
        >
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={Math.max(1, columns.length + (canSelect ? 1 : 0))}
                  style={{
                    color: '#666',
                    padding: '16px 8px',
                    textAlign: 'center',
                  }}
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              table
                .getRowModel()
                .rows.map((row) => (
                  <DataTableRow
                    key={row.id}
                    row={row}
                    enableDrag={canDrag}
                    enableSelection={canSelect}
                    onSelectionChange={row.getToggleSelectedHandler()}
                  />
                ))
            )}
          </tbody>
        </SortableContext>
      </table>
      {enablePagination && table.getPageCount() > 0 && (
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            gap: 12,
            justifyContent: 'flex-end',
            padding: '12px 8px',
          }}
        >
          {enablePageSizeSelector && (
            <label>
              Itens por página:{' '}
              <Dropdown
                aria-label="Itens por página"
                value={pagination.pageSize}
                onChange={(event) =>
                  table.setPageSize(Number(event.target.value))
                }
              >
                {[10, 20, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Dropdown>
            </label>
          )}
          <span>
            Página {pagination.pageIndex + 1}
            {` de ${table.getPageCount()}`}
          </span>
          <button
            type="button"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Anterior
          </button>
          {visiblePages.map((page, index) =>
            page === 'ellipsis' ? (
              <span key={`ellipsis-${index}`} aria-hidden="true">
                ...
              </span>
            ) : (
              <button
                key={page}
                type="button"
                aria-label={`Ir para a página ${page + 1}`}
                aria-current={
                  page === pagination.pageIndex ? 'page' : undefined
                }
                onClick={() => table.setPageIndex(page)}
                disabled={page === pagination.pageIndex}
              >
                {page + 1}
              </button>
            )
          )}
          <button
            type="button"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Próxima
          </button>
        </div>
      )}
    </DndContext>
  );
}

function getVisiblePages(
  pageCount: number,
  currentPageIndex: number
): Array<number | 'ellipsis'> {
  if (pageCount <= 5) {
    return Array.from({ length: pageCount }, (_, index) => index);
  }

  const lastPage = pageCount - 1;
  if (currentPageIndex === 0) {
    return [0, 1, 2, 'ellipsis', lastPage];
  }

  if (currentPageIndex >= pageCount - 3) {
    return [0, 'ellipsis', pageCount - 3, pageCount - 2, lastPage];
  }

  return [
    currentPageIndex - 1,
    currentPageIndex,
    currentPageIndex + 1,
    'ellipsis',
    lastPage,
  ];
}
